import type { ComponentProps } from "react";
import { useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { PassLeads } from "@/components/pass-leads";
import { ReportSkeleton } from "@/components/page-skeletons";
import { isNotFound, ViewFallback } from "@/components/page-states";
import { ReportView } from "@/components/report-view";
import { useT } from "@shared/client/locale";
import { Link } from "@shared/client/navigation";
import { usePageTitle, useView } from "@shared/client/view";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";

type ReportData = {
  run: { id: string; status: string; error_message: string | null; created_at: string | null };
  report: ComponentProps<typeof ReportView>["report"] | null;
  passLeads: ComponentProps<typeof PassLeads>["leads"];
};

export default function ReportPage() {
  const t = useT();
  const copy = t.reports.detail;
  usePageTitle(copy.pageTitle);
  const { id = "" } = useParams();
  const { data, error } = useView<ReportData>(`/api/views/reports/${encodeURIComponent(id)}`);
  const back = (
    <Button asChild className="mb-2 px-0 text-muted-foreground" size="sm" variant="link">
      <Link href="/app/reports"><ArrowLeft size={15} /> {copy.back}</Link>
    </Button>
  );

  if (!data) {
    return (
      <>
        {!isNotFound(error) && back}
        <ViewFallback error={error} notFound={{ ...copy.notFound, href: "/app/reports" }} skeleton={<ReportSkeleton />} subject={copy.subject} />
      </>
    );
  }
  const { run, report, passLeads } = data;

  return (
    <>
      {back}

      {report ? (
        <>
          <ReportView report={report} runId={run.id} />
          <PassLeads leads={passLeads} runAt={run.created_at ?? new Date(0).toISOString()} />
        </>
      ) : (
        <Alert variant="destructive">
          <AlertDescription>
          {run.status === "failed"
            ? copy.failed(run.error_message || copy.unknownReason)
            : run.status === "completed"
              ? copy.unreadable
              : copy.pending}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
